import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Button, Tag, Modal, Form, Input, InputNumber, Switch, Space, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined } from '@ant-design/icons';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import {
  getAllDeliverySlotsAction,
  createDeliverySlotAction,
  updateDeliverySlotAction,
  deleteDeliverySlotAction,
  toggleDeliverySlotAction,
} from '@app/store/slices/userSlice';

const EMPTY_FORM = {
  name: '',
  label: '',
  start_time: '',
  end_time: '',
  capacity: 50,
  is_active: true,
  sort_order: 0,
};

export const BasicTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const [slots, setSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchSlots = useCallback(() => {
    setLoading(true);
    dispatch(getAllDeliverySlotsAction())
      .unwrap()
      .then((res: any) => {
        setSlots(res.data || []);
      })
      .catch((err: any) => {
        notificationController.error({ message: err || 'Failed to load delivery slots' });
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const openAdd = () => {
    setEditingSlot(null);
    form.setFieldsValue(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (slot: any) => {
    setEditingSlot(slot);
    form.setFieldsValue({
      name: slot.name,
      label: slot.label,
      start_time: slot.start_time,
      end_time: slot.end_time,
      capacity: slot.capacity,
      is_active: slot.is_active,
      sort_order: slot.sort_order,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingSlot) {
        await dispatch(updateDeliverySlotAction({ id: editingSlot._id, payload: values })).unwrap();
        notificationController.success({ message: 'Slot updated successfully.' });
      } else {
        await dispatch(createDeliverySlotAction(values)).unwrap();
        notificationController.success({ message: 'Slot created successfully.' });
      }

      setModalOpen(false);
      fetchSlots();
    } catch (err: any) {
      // Ant Design throws { values, errorFields, outOfDate } when validation fails.
      // In that case the inline field errors are already visible — no toast needed.
      if (err?.errorFields) return;

      // Real API error — extract a string message before passing to the notification
      const msg =
        typeof err === 'string'
          ? err
          : err?.message || err?.error || 'Failed to save slot.';
      notificationController.error({ message: msg });
    } finally {
      setSubmitting(false);
    }
  };


  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteDeliverySlotAction(id)).unwrap();
      notificationController.success({ message: 'Slot deleted.' });
      fetchSlots();
    } catch (err: any) {
      const msg =
        typeof err === 'string'
          ? err
          : err?.message || err?.error || 'Failed to delete slot.';
      notificationController.error({ message: msg });
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await dispatch(toggleDeliverySlotAction(id)).unwrap();
      notificationController.success({ message: 'Slot status updated.' });
      fetchSlots();
    } catch (err: any) {
      notificationController.error({ message: err || 'Failed to toggle slot.' });
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: '#',
      dataIndex: 'sort_order',
      width: 60,
      sorter: (a, b) => a.sort_order - b.sort_order,
      render: (val) => <span style={{ color: '#888' }}>{val}</span>,
    },
    {
      title: 'Slot Name',
      dataIndex: 'name',
      width: 140,
      render: (val) => <strong>{val}</strong>,
    },
    {
      title: 'Time Range',
      dataIndex: 'label',
      width: 160,
      render: (val, rec) => (
        <Tooltip title={`${rec.start_time} – ${rec.end_time}`}>
          <Tag color="blue">{val}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Capacity / Day',
      dataIndex: 'capacity',
      width: 130,
      render: (val) => <span>{val} orders</span>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 110,
      render: (val) => <Tag color={val ? 'green' : 'default'}>{val ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions',
      width: 130,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? 'Deactivate' : 'Activate'}>
            <Button
              icon={<PoweroffOutlined />}
              size="small"
              type={record.is_active ? 'default' : 'primary'}
              onClick={() => handleToggle(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          Add Slot
        </Button>
      </div>

      <BaseTable
        columns={columns}
        dataSource={slots}
        rowKey="_id"
        loading={loading}
        pagination={false}
        bordered
        size="middle"
      />

      {/* Add / Edit Modal */}
      <Modal
        title={editingSlot ? 'Edit Delivery Slot' : 'Add Delivery Slot'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingSlot ? 'Save Changes' : 'Create Slot'}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Slot Name" rules={[{ required: true, message: 'Enter slot name' }]}>
            <Input placeholder="e.g. Morning" />
          </Form.Item>

          <Form.Item name="label" label="Display Label" rules={[{ required: true, message: 'Enter display label' }]}>
            <Input placeholder="e.g. 6 AM – 8 AM" />
          </Form.Item>

          <Form.Item label="Time Range (24 hrs format)" style={{ marginBottom: 0 }}>
            <Space>
              <Form.Item name="start_time" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                <Input placeholder="Start (24h e.g. 06:00)" style={{ width: 170 }} />
              </Form.Item>
              <span>–</span>
              <Form.Item name="end_time" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                <Input placeholder="End (24h e.g. 08:00)" style={{ width: 170 }} />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item name="capacity" label="Capacity per Day (max orders)" rules={[{ required: true, message: 'Enter capacity' }]} style={{ marginTop: 16 }}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="sort_order" label="Sort Order (lower = shown first)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
